import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useEnabledWheelByRarity } from '@sorare/core/src/hooks/useEnabledWheelByRarity';
import {
  GOLDEN_WHEEL_TICKET_BY_RARITY,
  WHEEL_TICKET_BY_RARITY,
} from '@sorare/core/src/lib/wheelCurrencies';

export const useWheelTickets = (
  { excludeCommonTickets }: { excludeCommonTickets: boolean } = {
    excludeCommonTickets: false,
  }
) => {
  const { currentUser } = useCurrentUserContext();
  const enabledWheels = useEnabledWheelByRarity();
  const isSeriesUser = useSeriesNav();
  const {
    wheel: { tasks, commonWheelTasks },
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();
  const effectiveTasks = isSeriesUser ? commonWheelTasks : tasks;
  const enabledCurrencies = new Set<string>();
  const entries = Object.entries(enabledWheels) as [string, boolean][];
  for (const [rarity, enabled] of entries) {
    if (
      enabled &&
      !(excludeCommonTickets && rarity === 'common') &&
      !(isSeriesUser && rarity !== 'common')
    ) {
      enabledCurrencies.add(
        WHEEL_TICKET_BY_RARITY[rarity as keyof typeof WHEEL_TICKET_BY_RARITY]
      );
      enabledCurrencies.add(
        GOLDEN_WHEEL_TICKET_BY_RARITY[
          rarity as keyof typeof GOLDEN_WHEEL_TICKET_BY_RARITY
        ]
      );
    }
  }

  const ticketCount =
    currentUser?.wheelTickets
      ?.filter(x => enabledCurrencies.has(x.currency))
      ?.reduce((acc, cur) => acc + cur.amount, 0) ?? 0;
  const ticketAvailable = effectiveTasks.some(
    t => t.aasmState === ManagerProgressionTaskState.COMPLETED
  );
  const canBeDisplayed = dailyTasksExpiresAt && effectiveTasks.some(t => t.id);
  const canClaimTicket = canBeDisplayed && ticketAvailable;

  return {
    canClaimTicket,
    ticketCount,
  };
};
